import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../ui/command";
import {
  Search as SearchIcon,
  CheckSquare,
  FolderKanban,
  User as UserIcon,
  MessageCircle,
  Plus,
  StickyNote,
  ArrowLeft,
  Hash,
  Layers,
} from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
  tasks: [],
  projects: [],
  users: [],
  comments: [],
  dms: [],
  channel_messages: [],
};

const PLACEHOLDERS = {
  root: "Ara veya oluştur… (görev, proje, kişi, not)",
  "task-title": "Görev başlığını yazın…",
  "task-project": "Proje ara…",
  "task-group": "Grup ara…",
  "project-name": "Proje adını yazın…",
};

export default function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  // root | task-title | task-project | task-group | project-name
  const [step, setStep] = useState("root");
  const [q, setQ] = useState("");
  const [results, setResults] = useState(EMPTY);
  const [projects, setProjects] = useState([]);
  const [groups, setGroups] = useState([]);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskProject, setTaskProject] = useState(null);
  const debounceRef = useRef(null);

  // ⌘K / Ctrl+K global shortcut.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const reset = () => {
    setStep("root");
    setQ("");
    setResults(EMPTY);
    setTaskTitle("");
    setTaskProject(null);
    setGroups([]);
  };
  const closeAll = () => {
    setOpen(false);
    reset();
  };
  const go = (path) => {
    closeAll();
    nav(path);
  };
  const backToRoot = () => {
    setStep("root");
    setQ("");
  };

  // Debounced global search — only on the root step.
  useEffect(() => {
    if (step !== "root" || !open) return;
    if (!q.trim()) {
      setResults(EMPTY);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q, limit: 5 } });
        setResults({ ...EMPTY, ...data });
      } catch {
        /* ignore */
      }
    }, 200);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, step, open]);

  const loadProjects = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data || []);
    } catch {
      setProjects([]);
    }
  };

  const startTaskFlow = async (title) => {
    setTaskTitle((title || "").trim());
    setQ("");
    await loadProjects();
    setStep((title || "").trim() ? "task-project" : "task-title");
  };

  const pickTaskProject = async (p) => {
    setTaskProject(p);
    setQ("");
    try {
      const { data } = await api.get(`/lists?project_id=${p.id}`);
      const ls = data || [];
      if (ls.length === 0) {
        toast.error("Bu listede grup yok — önce bir proje grubu oluşturun");
        return;
      }
      if (ls.length === 1) {
        await createTask(p, ls[0]);
        return;
      }
      setGroups(ls);
      setStep("task-group");
    } catch {
      toast.error("Gruplar yüklenemedi");
    }
  };

  const createTask = async (p, list) => {
    try {
      const { data } = await api.post("/tasks", {
        project_id: p.id,
        list_id: list.id,
        title: taskTitle,
      });
      toast.success("Görev oluşturuldu");
      go(`/app/projects/${p.id}?task=${data.id}`);
    } catch {
      toast.error("Görev oluşturulamadı");
    }
  };

  const createProject = async (name) => {
    try {
      const { data } = await api.post("/projects", { name: name.trim() });
      toast.success("Proje oluşturuldu");
      window.dispatchEvent(new Event("serpix:projects-changed"));
      go(`/app/projects/${data.id}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail === "cannot_create_projects"
          ? "Proje oluşturma yetkiniz yok"
          : "Proje oluşturulamadı",
      );
    }
  };

  const addQuickNote = async (text) => {
    try {
      await api.post("/quick-notes", { text: text.trim() });
      toast.success("Hızlı not eklendi");
      closeAll();
    } catch {
      toast.error("Not eklenemedi");
    }
  };

  const openChat = (kind, id, name) => {
    closeAll();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("dm-open", { detail: { kind, id, name } }),
      );
    }, 30);
  };

  const norm = (s) => (s || "").toLocaleLowerCase("tr");
  const filteredProjects = projects.filter((p) => norm(p.name).includes(norm(q)));
  const filteredGroups = groups.filter((l) => norm(l.name).includes(norm(q)));
  const backItem = (
    <CommandItem value="__back" onSelect={backToRoot} data-testid="palette-back">
      <ArrowLeft className="mr-2 text-zinc-400" />
      Geri
    </CommandItem>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="command-palette-trigger"
        className="inline-flex h-9 items-center gap-1.5 rounded-full border border-zinc-200 px-3 text-xs text-zinc-500 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
      >
        <SearchIcon className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">Ara</span>
        <kbd className="hidden rounded bg-zinc-100 px-1 py-0.5 text-[10px] font-semibold text-zinc-500 sm:inline">
          ⌘K
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) reset();
        }}
        shouldFilter={false}
      >
        <CommandInput
          value={q}
          onValueChange={setQ}
          placeholder={PLACEHOLDERS[step]}
          data-testid="palette-input"
          onKeyDown={(e) => {
            if (e.key === "Escape" && step !== "root") {
              e.preventDefault();
              e.stopPropagation();
              backToRoot();
            }
          }}
        />
        <CommandList className="max-h-[420px]" data-testid="palette-list">
          {step === "root" && (
            <>
              <CommandGroup heading="Oluştur">
                <CommandItem
                  value="__create-task"
                  onSelect={() => startTaskFlow(q)}
                  data-testid="palette-create-task"
                >
                  <Plus className="mr-2 text-violet-600" />
                  Görev oluştur{q.trim() ? `: “${q.trim()}”` : "…"}
                </CommandItem>
                <CommandItem
                  value="__create-project"
                  onSelect={() => {
                    if (q.trim()) createProject(q);
                    else {
                      setQ("");
                      setStep("project-name");
                    }
                  }}
                  data-testid="palette-create-project"
                >
                  <FolderKanban className="mr-2 text-teal-600" />
                  Proje oluştur{q.trim() ? `: “${q.trim()}”` : "…"}
                </CommandItem>
                {q.trim() && (
                  <CommandItem
                    value="__create-note"
                    onSelect={() => addQuickNote(q)}
                    data-testid="palette-create-note"
                  >
                    <StickyNote className="mr-2 text-amber-600" />
                    Hızlı not ekle: “{q.trim()}”
                  </CommandItem>
                )}
              </CommandGroup>

              {results.tasks.length > 0 && (
                <CommandGroup heading="Görevler">
                  {results.tasks.map((tsk) => (
                    <CommandItem
                      key={tsk.id}
                      value={`task-${tsk.id}`}
                      onSelect={() =>
                        go(`/app/projects/${tsk.project_id}?task=${tsk.id}`)
                      }
                      data-testid={`palette-task-${tsk.id}`}
                    >
                      <CheckSquare className="mr-2 text-zinc-400" />
                      <span className="truncate">{tsk.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {results.projects.length > 0 && (
                <CommandGroup heading="Projeler">
                  {results.projects.map((p) => (
                    <CommandItem
                      key={p.id}
                      value={`proj-${p.id}`}
                      onSelect={() => go(`/app/projects/${p.id}`)}
                      data-testid={`palette-project-${p.id}`}
                    >
                      <Hash className="mr-2 text-zinc-400" />
                      <span className="truncate">{p.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {results.users.length > 0 && (
                <CommandGroup heading="Kişiler">
                  {results.users.map((u) => (
                    <CommandItem
                      key={u.id}
                      value={`user-${u.id}`}
                      onSelect={() => openChat("dm", u.id, u.name)}
                      data-testid={`palette-user-${u.id}`}
                    >
                      <UserIcon className="mr-2 text-zinc-400" />
                      <span className="truncate">{u.name}</span>
                      <span className="ml-2 truncate text-xs text-zinc-400">
                        {u.email}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {results.comments.length > 0 && (
                <CommandGroup heading="Yorumlar">
                  {results.comments.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={`comment-${c.id}`}
                      onSelect={() =>
                        go(`/app/projects/${c.project_id}?task=${c.task_id}`)
                      }
                    >
                      <MessageCircle className="mr-2 text-zinc-400" />
                      <span className="truncate">{c.body_text || c.body}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {results.dms.length > 0 && (
                <CommandGroup heading="Mesajlar">
                  {results.dms.map((m) => (
                    <CommandItem
                      key={m.id}
                      value={`dm-${m.id}`}
                      onSelect={() => openChat("dm", m.peer_id, m.peer_name)}
                    >
                      <MessageCircle className="mr-2 text-zinc-400" />
                      <span className="truncate">
                        {m.peer_name}: {m.body}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {results.channel_messages.length > 0 && (
                <CommandGroup heading="Kanal Mesajları">
                  {results.channel_messages.map((m) => (
                    <CommandItem
                      key={m.id}
                      value={`ch-${m.id}`}
                      onSelect={() =>
                        openChat("channel", m.channel_id, m.channel_name)
                      }
                    >
                      <MessageCircle className="mr-2 text-zinc-400" />
                      <span className="truncate">
                        #{m.channel_name}: {m.body}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          )}

          {step === "task-title" && (
            <CommandGroup heading="Yeni Görev — başlığı yazıp Enter'a basın">
              <CommandItem
                value="__task-title-submit"
                disabled={!q.trim()}
                onSelect={() => {
                  setTaskTitle(q.trim());
                  setQ("");
                  setStep("task-project");
                }}
                data-testid="palette-task-title-submit"
              >
                <Plus className="mr-2 text-violet-600" />
                {q.trim() ? `“${q.trim()}” için liste seç →` : "Görev başlığı yazın…"}
              </CommandItem>
              {backItem}
            </CommandGroup>
          )}

          {step === "task-project" && (
            <CommandGroup heading={`“${taskTitle}” hangi To Do List'e eklensin?`}>
              {filteredProjects.map((p) => (
                <CommandItem
                  key={p.id}
                  value={`pickproj-${p.id}`}
                  onSelect={() => pickTaskProject(p)}
                  data-testid={`palette-pick-project-${p.id}`}
                >
                  <Hash className="mr-2 text-zinc-400" />
                  <span className="truncate">{p.name}</span>
                  {p.kind === "general" && (
                    <span className="ml-2 rounded bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold text-teal-700">
                      Genel
                    </span>
                  )}
                </CommandItem>
              ))}
              {filteredProjects.length === 0 && (
                <CommandItem value="__noproj" disabled>
                  Eşleşen proje yok
                </CommandItem>
              )}
              <CommandSeparator />
              {backItem}
            </CommandGroup>
          )}

          {step === "task-group" && (
            <CommandGroup
              heading={`${taskProject?.name || ""} — hangi gruba eklensin?`}
            >
              {filteredGroups.map((l) => (
                <CommandItem
                  key={l.id}
                  value={`pickgroup-${l.id}`}
                  onSelect={() => createTask(taskProject, l)}
                  data-testid={`palette-pick-group-${l.id}`}
                >
                  <Layers className="mr-2 text-zinc-400" />
                  <span className="truncate">{l.name}</span>
                </CommandItem>
              ))}
              <CommandSeparator />
              {backItem}
            </CommandGroup>
          )}

          {step === "project-name" && (
            <CommandGroup heading="Yeni Proje — adını yazıp Enter'a basın">
              <CommandItem
                value="__project-name-submit"
                disabled={!q.trim()}
                onSelect={() => createProject(q)}
                data-testid="palette-project-name-submit"
              >
                <FolderKanban className="mr-2 text-teal-600" />
                {q.trim() ? `“${q.trim()}” projesini oluştur` : "Proje adı yazın…"}
              </CommandItem>
              {backItem}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
